import { useRecordCbtMonitoringEventMutation } from '@/types/vue-apollo-helpers';
import {
  CbtMonitoringEventSeverityEnum,
  CbtMonitoringEventTypeEnum,
} from '@/types/composition-functions';

interface AntiCheatHandle {
  attach: () => void;
  detach: () => void;
}

export interface AntiCheatOptions {
  /**
   * Getter for the local webcam MediaStream, if available. Used by the
   * virtual-camera and audio-voice detectors. Optional — without it, those
   * detectors are no-ops.
   */
  webcamStream?: () => MediaStream | null;
  /** Pixels of chrome that, when exceeded, are treated as devtools open. */
  devtoolsSizeThresholdPx?: number;
  /** Sustained ms of pointer-outside-viewport before firing. */
  pointerLeaveThresholdMs?: number;
  /** Linear RMS [0..1] above which audio is considered "voice". */
  audioVoiceRmsThreshold?: number;
  /** Sustained ms above threshold before firing. */
  audioVoiceMinDurationMs?: number;
  /** Min ms between successive AUDIO_VOICE_DETECTED events. */
  audioVoiceCooldownMs?: number;
  /** Min ms between successive RIGHT_CLICK_BLOCKED events (spam guard). */
  rightClickCooldownMs?: number;
}

const DEFAULT_OPTIONS = {
  devtoolsSizeThresholdPx: 160,
  pointerLeaveThresholdMs: 8_000,
  audioVoiceRmsThreshold: 0.08,
  audioVoiceMinDurationMs: 1_500,
  audioVoiceCooldownMs: 30_000,
  rightClickCooldownMs: 2_000,
} as const;

const VIRTUAL_CAMERA_LABEL_PATTERN =
  /obs|snap[\s-]?cam|manycam|xsplit|streamlabs|vcam|virtual\s?cam|nvidia\s?broadcast|camo/i;

/**
 * Student-side. Wires browser DOM/Web APIs that indicate possible cheating
 * and forwards them to the backend via `recordCbtMonitoringEvent`.
 *
 * Caller is responsible for calling `attach()` once the monitoring session
 * has been created and `detach()` on submit / unmount.
 */
export function useAntiCheatDetection(
  monitoringSessionId: string,
  options: AntiCheatOptions = {},
): AntiCheatHandle {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { mutate } = useRecordCbtMonitoringEventMutation();

  const send = (
    eventType: CbtMonitoringEventTypeEnum,
    severity: CbtMonitoringEventSeverityEnum,
    payload?: Record<string, unknown>,
  ) => {
    mutate({
      input: {
        monitoring_session_id: monitoringSessionId,
        event_type: eventType,
        severity,
        occurred_at: new Date().toISOString(),
        payload: payload ?? null,
      },
    }).catch(() => {
      // Anti-cheat events are best-effort. Don't blow up the exam UI on a
      // network blip — failures here are themselves often signal of disconnect
      // and the network event listener will record that separately.
    });
  };

  // ----- Existing detectors -----

  const onVisibilityChange = () => {
    if (document.hidden) {
      send(CbtMonitoringEventTypeEnum.VisibilityLost, CbtMonitoringEventSeverityEnum.Warn);
    } else {
      send(CbtMonitoringEventTypeEnum.VisibilityRegained, CbtMonitoringEventSeverityEnum.Info);
    }
  };

  const onBlur = () =>
    send(CbtMonitoringEventTypeEnum.FocusLost, CbtMonitoringEventSeverityEnum.Warn);
  const onFocus = () =>
    send(CbtMonitoringEventTypeEnum.FocusRegained, CbtMonitoringEventSeverityEnum.Info);

  const onCopy = () =>
    send(CbtMonitoringEventTypeEnum.CopyAttempt, CbtMonitoringEventSeverityEnum.Info);
  const onPaste = () =>
    send(CbtMonitoringEventTypeEnum.PasteAttempt, CbtMonitoringEventSeverityEnum.Warn);

  const onFullscreenChange = () => {
    if (!document.fullscreenElement) {
      send(CbtMonitoringEventTypeEnum.FullscreenExit, CbtMonitoringEventSeverityEnum.Critical);
    }
  };

  const onOffline = () =>
    send(CbtMonitoringEventTypeEnum.NetworkDisconnect, CbtMonitoringEventSeverityEnum.Warn);
  const onOnline = () =>
    send(CbtMonitoringEventTypeEnum.NetworkReconnect, CbtMonitoringEventSeverityEnum.Info);

  // ----- New detectors -----

  // DEVTOOLS_OPENED: chrome-size heuristic. Browsers don't expose devtools
  // state directly, but opening the inspector usually shrinks innerWidth or
  // innerHeight by ~160+ px. We only fire once per "open → close → open"
  // cycle so we don't spam during window drags.
  let devtoolsOpen = false;
  const checkDevtools = () => {
    const dw = window.outerWidth - window.innerWidth;
    const dh = window.outerHeight - window.innerHeight;
    const open = dw > opts.devtoolsSizeThresholdPx || dh > opts.devtoolsSizeThresholdPx;
    if (open && !devtoolsOpen) {
      devtoolsOpen = true;
      send(
        CbtMonitoringEventTypeEnum.DevtoolsOpened,
        CbtMonitoringEventSeverityEnum.Warn,
        { method: 'window_size', delta_w: dw, delta_h: dh },
      );
    } else if (!open && devtoolsOpen) {
      devtoolsOpen = false;
    }
  };

  // RIGHT_CLICK_BLOCKED: block context menu + record. Throttle, since a user
  // pounding the mouse button shouldn't generate hundreds of rows.
  let lastRightClickAt = 0;
  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastRightClickAt < opts.rightClickCooldownMs) return;
    lastRightClickAt = now;
    const target = (e.target as HTMLElement | null)?.tagName?.toLowerCase() ?? 'unknown';
    send(
      CbtMonitoringEventTypeEnum.RightClickBlocked,
      CbtMonitoringEventSeverityEnum.Info,
      { target },
    );
  };

  // KEYBOARD_SHORTCUT_BLOCKED: catch the obvious cheat / inspect / save / print
  // shortcuts. Note: browser-level shortcuts (Cmd+Tab, Cmd+W) can't be
  // intercepted from JS — those are the OS / browser chrome.
  const isBlockedShortcut = (e: KeyboardEvent): string | null => {
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (e.key === 'F12') return 'F12';
    if (mod && e.shiftKey && (key === 'i' || key === 'j' || key === 'c')) {
      return `${e.ctrlKey ? 'Ctrl' : 'Meta'}+Shift+${e.key.toUpperCase()}`;
    }
    if (mod && (key === 'u' || key === 's' || key === 'p')) {
      return `${e.ctrlKey ? 'Ctrl' : 'Meta'}+${e.key.toUpperCase()}`;
    }
    return null;
  };
  const onKeyDown = (e: KeyboardEvent) => {
    const combo = isBlockedShortcut(e);
    if (!combo) return;
    e.preventDefault();
    send(
      CbtMonitoringEventTypeEnum.KeyboardShortcutBlocked,
      CbtMonitoringEventSeverityEnum.Info,
      { combo },
    );
  };

  // MULTI_DISPLAY_DETECTED: window.screen.isExtended is the cheap path. The
  // richer getScreenDetails() requires a Window Management permission prompt
  // we can't show mid-exam — use it only if the user has already granted.
  const checkMultiDisplay = async() => {
    const screen = window.screen as Screen & { isExtended?: boolean };
    let extended = Boolean(screen.isExtended);
    let screenCount: number | undefined;

    const nav = navigator as Navigator & {
      windowManagement?: { getScreenDetails?: () => Promise<{ screens: unknown[] }> };
    };
    const getDetails = nav.windowManagement?.getScreenDetails;
    if (getDetails) {
      try {
        const details = await getDetails.call(nav.windowManagement);
        screenCount = details.screens.length;
        if (screenCount > 1) extended = true;
      } catch {
        // Permission not granted or API rejected — fall back to isExtended only.
      }
    }

    if (extended) {
      send(
        CbtMonitoringEventTypeEnum.MultiDisplayDetected,
        CbtMonitoringEventSeverityEnum.Warn,
        { screen_count: screenCount ?? null, via: getDetails ? 'window_management' : 'is_extended' },
      );
    }
  };

  // VIRTUAL_CAMERA_DETECTED: one-shot inspection after the producer is
  // publishing. Matches the active video track's label / device name against
  // a list of common virtual-camera vendors.
  const checkVirtualCamera = async() => {
    const stream = opts.webcamStream?.();
    if (!stream) return;
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return;

    const labelGuesses: string[] = [];
    if (videoTrack.label) labelGuesses.push(videoTrack.label);

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const settings = videoTrack.getSettings();
      const matched = devices.find(
        (d) => d.kind === 'videoinput' && d.deviceId === settings.deviceId,
      );
      if (matched?.label) labelGuesses.push(matched.label);
    } catch {
      // enumerateDevices can throw when permission is denied; the track label
      // we already captured is still useful.
    }

    const offender = labelGuesses.find((label) => VIRTUAL_CAMERA_LABEL_PATTERN.test(label));
    if (offender) {
      send(
        CbtMonitoringEventTypeEnum.VirtualCameraDetected,
        CbtMonitoringEventSeverityEnum.Critical,
        { device_label: offender },
      );
    }
  };

  // POINTER_LEAVE_LONG: pointer left the viewport for longer than the
  // threshold. A short blip (grabbing a sip of water) shouldn't fire.
  let pointerLeftAt: number | null = null;
  let pointerTimer: ReturnType<typeof setTimeout> | null = null;
  const onMouseLeave = () => {
    pointerLeftAt = Date.now();
    if (pointerTimer) clearTimeout(pointerTimer);
    pointerTimer = setTimeout(() => {
      if (pointerLeftAt) {
        send(
          CbtMonitoringEventTypeEnum.PointerLeaveLong,
          CbtMonitoringEventSeverityEnum.Info,
          { duration_ms: Date.now() - pointerLeftAt },
        );
      }
    }, opts.pointerLeaveThresholdMs);
  };
  const onMouseEnter = () => {
    pointerLeftAt = null;
    if (pointerTimer) {
      clearTimeout(pointerTimer);
      pointerTimer = null;
    }
  };

  // AUDIO_VOICE_DETECTED: Web Audio analyser sampling RMS. We sample every
  // 200ms; once the mic has been "loud" continuously for `audioVoiceMinDurationMs`
  // we fire one event, then enter a cooldown so we don't spam.
  let audioContext: AudioContext | null = null;
  let audioSource: MediaStreamAudioSourceNode | null = null;
  let audioAnalyser: AnalyserNode | null = null;
  let audioTimer: ReturnType<typeof setInterval> | null = null;
  let voiceStartedAt: number | null = null;
  let lastVoiceEventAt = 0;

  const startAudioMonitor = () => {
    const stream = opts.webcamStream?.();
    if (!stream) return;
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;

    try {
      const Ctor: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioContext = new Ctor();
      audioSource = audioContext.createMediaStreamSource(stream);
      audioAnalyser = audioContext.createAnalyser();
      audioAnalyser.fftSize = 1024;
      audioSource.connect(audioAnalyser);
    } catch {
      audioContext = null;
      return;
    }

    const buffer = new Float32Array(audioAnalyser.fftSize);
    audioTimer = setInterval(() => {
      if (!audioAnalyser) return;
      audioAnalyser.getFloatTimeDomainData(buffer);
      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) {
        const sample = buffer[i] ?? 0;
        sumSquares += sample * sample;
      }
      const rms = Math.sqrt(sumSquares / buffer.length);

      const now = Date.now();
      if (rms > opts.audioVoiceRmsThreshold) {
        if (voiceStartedAt === null) voiceStartedAt = now;
        const sustainedFor = now - voiceStartedAt;
        if (
          sustainedFor >= opts.audioVoiceMinDurationMs &&
          now - lastVoiceEventAt >= opts.audioVoiceCooldownMs
        ) {
          lastVoiceEventAt = now;
          send(
            CbtMonitoringEventTypeEnum.AudioVoiceDetected,
            CbtMonitoringEventSeverityEnum.Warn,
            { peak_rms: Number(rms.toFixed(3)), sustained_ms: sustainedFor },
          );
        }
      } else {
        voiceStartedAt = null;
      }
    }, 200);
  };

  const stopAudioMonitor = () => {
    if (audioTimer) {
      clearInterval(audioTimer);
      audioTimer = null;
    }
    try {
      audioSource?.disconnect();
      audioAnalyser?.disconnect();
      audioContext?.close();
    } catch {
      // ignore: AudioContext can throw on close in some browsers
    }
    audioContext = null;
    audioSource = null;
    audioAnalyser = null;
    voiceStartedAt = null;
  };

  // ----- Lifecycle -----

  let attached = false;

  const attach = () => {
    if (attached) return;
    attached = true;
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('copy', onCopy);
    document.addEventListener('paste', onPaste);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', checkDevtools);
    document.documentElement.addEventListener('mouseleave', onMouseLeave);
    document.documentElement.addEventListener('mouseenter', onMouseEnter);

    // One-shot checks at session start.
    checkDevtools();
    void checkMultiDisplay();
    void checkVirtualCamera();
    startAudioMonitor();
  };

  const detach = () => {
    if (!attached) return;
    attached = false;
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('focus', onFocus);
    document.removeEventListener('copy', onCopy);
    document.removeEventListener('paste', onPaste);
    document.removeEventListener('fullscreenchange', onFullscreenChange);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('online', onOnline);

    document.removeEventListener('contextmenu', onContextMenu);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', checkDevtools);
    document.documentElement.removeEventListener('mouseleave', onMouseLeave);
    document.documentElement.removeEventListener('mouseenter', onMouseEnter);

    if (pointerTimer) {
      clearTimeout(pointerTimer);
      pointerTimer = null;
    }
    stopAudioMonitor();
  };

  return { attach, detach };
}
